import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { AvisoSistema, AvisoSistemaConfirmacao } from '../../types';
import { avisoAlvejaPerfil, PerfilParaAlvo } from '../../src/lib/avisoAlvejaPerfil';
import { DEPARTAMENTOS, DEPT_COLOR } from '../../src/lib/departamentos';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { Megaphone, Plus, X, Loader2, Pencil, Archive, ChevronDown, ChevronRight, ExternalLink, Upload, AlertTriangle, Sparkles } from 'lucide-react';

const RECIPIENT_ROLES = ['adm', 'gerente', 'analista', 'vendedor', 'gerente_parcerias'];

type ConfirmacaoComPerfil = AvisoSistemaConfirmacao & { profiles: { full_name: string } | null };

const STATUS_CONFIG: Record<AvisoSistema['status'], { label: string; color: string }> = {
    rascunho: { label: 'Rascunho', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
    publicado: { label: 'Publicado', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    arquivado: { label: 'Arquivado', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500' },
};

const MODELO_CONFIG: Record<AvisoSistema['modelo'], { label: string; color: string; Icon: typeof AlertTriangle }> = {
    risco: { label: 'Risco', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', Icon: AlertTriangle },
    novidade: { label: 'Novidade', color: 'bg-primary/10 text-primary dark:bg-primary/20', Icon: Sparkles },
};

const BUCKET_ANEXOS = 'avisos-anexos';
const TAMANHO_MAXIMO_ANEXO = 10 * 1024 * 1024;
const TODO_STAFF = '';

const dataHoraBr = (iso?: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const paraDatetimeLocal = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface FormAviso {
    titulo: string;
    modelo: AvisoSistema['modelo'];
    mensagem: string;
    bloqueante: boolean;
    url_anexo: string;
    data_inicio: string;
    data_fim: string;
    departamento_alvo: string;
    unidade_geradora_site_id: string;
}

const FORM_VAZIO: FormAviso = { titulo: '', modelo: 'novidade', mensagem: '', bloqueante: true, url_anexo: '', data_inicio: '', data_fim: '', departamento_alvo: TODO_STAFF, unidade_geradora_site_id: '' };

const AvisosSistemaView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();
    const souAdm = profile?.role === 'adm';

    const [avisos, setAvisos] = useState<AvisoSistema[]>([]);
    const [confirmacoes, setConfirmacoes] = useState<AvisoSistemaConfirmacao[]>([]);
    const [staffAlvo, setStaffAlvo] = useState<PerfilParaAlvo[]>([]);
    const [loading, setLoading] = useState(true);

    const [expandidoId, setExpandidoId] = useState<string | null>(null);
    const [detalhes, setDetalhes] = useState<Map<string, ConfirmacaoComPerfil[]>>(new Map());
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState<AvisoSistema | null>(null);
    const [form, setForm] = useState<FormAviso>(FORM_VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [enviandoAnexo, setEnviandoAnexo] = useState(false);

    const [confirmarPublicar, setConfirmarPublicar] = useState<{ id?: string } | null>(null);
    const [confirmarArquivar, setConfirmarArquivar] = useState<AvisoSistema | null>(null);

    const carregar = async () => {
        setLoading(true);
        try {
            const [{ data: avisosData, error: errAvisos }, { data: confirmData, error: errConfirm }, { data: staffData, error: errStaff }] = await Promise.all([
                supabase.from('avisos_sistema').select('*, profiles(full_name)').order('criado_em', { ascending: false }),
                supabase.from('avisos_sistema_confirmacoes').select('id, aviso_id, profile_id, visualizado_em, confirmado_em'),
                // status='active': mesmo filtro usado nas demais telas que contam/listam staff
                // (RotasView, MentionTextarea...) — sem ele, colaborador desligado infla o
                // denominador de "alcance" pra sempre, e o X/Y nunca bate com quem o gate
                // (AvisosSistemaGate) de fato consegue bloquear, já que quem está inativo
                // nem loga pra ver o aviso.
                supabase.from('profiles').select('departamento, departamentos').in('role', RECIPIENT_ROLES).eq('status', 'active'),
            ]);
            if (errAvisos) throw errAvisos;
            if (errConfirm) throw errConfirm;
            if (errStaff) throw errStaff;
            setAvisos((avisosData || []) as AvisoSistema[]);
            setConfirmacoes((confirmData || []) as AvisoSistemaConfirmacao[]);
            setStaffAlvo((staffData || []) as PerfilParaAlvo[]);
        } catch (err) {
            console.error('Erro ao carregar avisos do sistema:', err);
            addToast('Erro ao carregar avisos.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const statsPorAviso = useMemo(() => {
        const mapa = new Map<string, { visualizaram: number; confirmaram: number; alcance: number }>();
        avisos.forEach((aviso) => {
            const alcance = staffAlvo.filter((p) => avisoAlvejaPerfil({ departamento_alvo: aviso.departamento_alvo ?? null }, p)).length;
            mapa.set(aviso.id, { visualizaram: 0, confirmaram: 0, alcance });
        });
        confirmacoes.forEach((c) => {
            const atual = mapa.get(c.aviso_id);
            if (!atual) return;
            if (c.visualizado_em) atual.visualizaram += 1;
            if (c.confirmado_em) atual.confirmaram += 1;
        });
        return mapa;
    }, [avisos, confirmacoes, staffAlvo]);

    const abrirNovo = () => {
        setEditando(null);
        setForm(FORM_VAZIO);
        setModalAberto(true);
    };

    const abrirEdicao = (aviso: AvisoSistema) => {
        setEditando(aviso);
        setForm({
            titulo: aviso.titulo,
            modelo: aviso.modelo,
            mensagem: aviso.mensagem,
            bloqueante: aviso.bloqueante,
            url_anexo: aviso.url_anexo || '',
            data_inicio: paraDatetimeLocal(aviso.data_inicio),
            data_fim: paraDatetimeLocal(aviso.data_fim),
            departamento_alvo: aviso.departamento_alvo || TODO_STAFF,
            unidade_geradora_site_id: aviso.unidade_geradora_site_id || '',
        });
        setModalAberto(true);
    };

    const enviarAnexo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > TAMANHO_MAXIMO_ANEXO) {
            addToast('Arquivo muito grande (máximo 10 MB).', 'error');
            return;
        }
        setEnviandoAnexo(true);
        try {
            const path = `${crypto.randomUUID()}-${file.name}`;
            const { error: upErr } = await supabase.storage.from(BUCKET_ANEXOS).upload(path, file);
            if (upErr) throw upErr;
            const { data } = supabase.storage.from(BUCKET_ANEXOS).getPublicUrl(path);
            setForm((prev) => ({ ...prev, url_anexo: data.publicUrl }));
            addToast('Arquivo enviado.', 'success');
        } catch (err) {
            console.error('Erro ao enviar anexo:', err);
            addToast('Erro ao enviar o arquivo.', 'error');
        } finally {
            setEnviandoAnexo(false);
        }
    };

    const payloadDoForm = () => ({
        titulo: form.titulo.trim(),
        modelo: form.modelo,
        mensagem: form.mensagem.trim(),
        bloqueante: form.bloqueante,
        url_anexo: form.url_anexo.trim() || null,
        data_inicio: form.data_inicio ? new Date(form.data_inicio).toISOString() : null,
        data_fim: form.data_fim ? new Date(form.data_fim).toISOString() : null,
        departamento_alvo: form.departamento_alvo || null,
        unidade_geradora_site_id: form.unidade_geradora_site_id.trim() || null,
    });

    const salvar = async (status: 'rascunho' | 'publicado') => {
        if (!form.titulo.trim() || !form.mensagem.trim()) {
            addToast('Preencha título e mensagem.', 'error');
            return;
        }
        setSalvando(true);
        try {
            const payload = payloadDoForm();

            if (!editando) {
                const { error } = await supabase.from('avisos_sistema').insert({ ...payload, status, criado_por: profile!.id });
                if (error) throw error;
            } else if (editando.status === 'rascunho') {
                const { error } = await supabase.from('avisos_sistema').update({ ...payload, status }).eq('id', editando.id);
                if (error) throw error;
            } else {
                const { error: errInsert } = await supabase
                    .from('avisos_sistema')
                    .insert({ ...payload, status, criado_por: profile!.id, versao_anterior_id: editando.id });
                if (errInsert) throw errInsert;
                const { error: errArquivar } = await supabase
                    .from('avisos_sistema')
                    .update({ status: 'arquivado' })
                    .eq('id', editando.id);
                if (errArquivar) throw errArquivar;
            }

            addToast(status === 'publicado' ? 'Aviso publicado.' : 'Rascunho salvo.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'salvar_aviso_sistema', origem: 'acao' });
            setModalAberto(false);
            await carregar();
        } catch (err) {
            console.error('Erro ao salvar aviso do sistema:', err);
            addToast('Erro ao salvar o aviso.', 'error');
        } finally {
            setSalvando(false);
            setConfirmarPublicar(null);
        }
    };

    const publicarRascunho = async (id: string) => {
        try {
            const { error } = await supabase.from('avisos_sistema').update({ status: 'publicado' }).eq('id', id);
            if (error) throw error;
            addToast('Aviso publicado.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'publicar_aviso_sistema', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao publicar aviso do sistema:', err);
            addToast('Erro ao publicar o aviso.', 'error');
        } finally {
            setConfirmarPublicar(null);
        }
    };

    const arquivar = async (aviso: AvisoSistema) => {
        try {
            const { error } = await supabase.from('avisos_sistema').update({ status: 'arquivado' }).eq('id', aviso.id);
            if (error) throw error;
            addToast('Aviso arquivado.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'arquivar_aviso_sistema', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao arquivar aviso do sistema:', err);
            addToast('Erro ao arquivar o aviso.', 'error');
        } finally {
            setConfirmarArquivar(null);
        }
    };

    const alternarDetalhe = async (avisoId: string) => {
        if (expandidoId === avisoId) {
            setExpandidoId(null);
            return;
        }
        setExpandidoId(avisoId);
        if (detalhes.has(avisoId)) return;
        setCarregandoDetalhe(true);
        try {
            const { data, error } = await supabase
                .from('avisos_sistema_confirmacoes')
                .select('id, aviso_id, profile_id, visualizado_em, confirmado_em, profiles(full_name)')
                .eq('aviso_id', avisoId)
                .order('visualizado_em', { ascending: false });
            if (error) throw error;
            setDetalhes((prev) => new Map(prev).set(avisoId, (data || []) as unknown as ConfirmacaoComPerfil[]));
        } catch (err) {
            console.error('Erro ao carregar confirmações do aviso:', err);
            addToast('Erro ao carregar quem visualizou/confirmou.', 'error');
        } finally {
            setCarregandoDetalhe(false);
        }
    };

    if (loading) return <LoadingSpinner label="Carregando avisos..." />;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Megaphone size={22} className="text-primary" />
                        Avisos do Sistema
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Comunicados obrigatórios para a equipe interna, com confirmação de leitura.
                    </p>
                </div>
                {souAdm && (
                    <button
                        onClick={abrirNovo}
                        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-colors"
                    >
                        <Plus size={16} />
                        Novo aviso
                    </button>
                )}
            </div>

            {avisos.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-12 text-center">
                    <Megaphone size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">Nenhum aviso criado ainda.</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-300 text-xs font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Aviso</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Alvo</th>
                                    <th className="px-6 py-4">Criado por</th>
                                    <th className="px-6 py-4">Confirmações</th>
                                    <th className="px-6 py-4 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {avisos.map((aviso) => {
                                    const stats = statsPorAviso.get(aviso.id) || { visualizaram: 0, confirmaram: 0, alcance: 0 };
                                    const progresso = stats.alcance > 0 ? Math.round((stats.confirmaram / stats.alcance) * 100) : 0;
                                    const expandido = expandidoId === aviso.id;
                                    const modeloCfg = MODELO_CONFIG[aviso.modelo];
                                    const criadoPorNome = (aviso as unknown as { profiles: { full_name: string } | null }).profiles?.full_name;
                                    return (
                                        <React.Fragment key={aviso.id}>
                                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 max-w-xs">
                                                    <button onClick={() => alternarDetalhe(aviso.id)} className="flex items-start gap-2 text-left">
                                                        {expandido ? <ChevronDown size={16} className="mt-0.5 shrink-0 text-gray-400" /> : <ChevronRight size={16} className="mt-0.5 shrink-0 text-gray-400" />}
                                                        <div>
                                                            <p className="font-semibold text-gray-900 dark:text-white truncate">{aviso.titulo}</p>
                                                            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full mt-1 ${modeloCfg.color}`}>
                                                                <modeloCfg.Icon size={11} />
                                                                {modeloCfg.label}
                                                            </span>
                                                        </div>
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_CONFIG[aviso.status].color}`}>
                                                        {STATUS_CONFIG[aviso.status].label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${aviso.departamento_alvo ? (DEPT_COLOR[aviso.departamento_alvo] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300') : 'bg-primary/10 text-primary dark:bg-primary/20'}`}>
                                                        {aviso.departamento_alvo || 'Todo o staff'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                                    {criadoPorNome || '—'}
                                                </td>
                                                <td className="px-6 py-4 min-w-[160px]">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progresso}%` }} />
                                                        </div>
                                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">
                                                            {stats.confirmaram}/{stats.alcance}
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-gray-400 mt-1">{stats.visualizaram} visualizaram</p>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    {souAdm && (
                                                        <div className="flex justify-end gap-2">
                                                            {aviso.status !== 'arquivado' && (
                                                                <button onClick={() => abrirEdicao(aviso)} title="Editar" className="p-2 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                                                    <Pencil size={16} />
                                                                </button>
                                                            )}
                                                            {aviso.status === 'rascunho' && (
                                                                <button onClick={() => setConfirmarPublicar({ id: aviso.id })} title="Publicar" className="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                                                    <Megaphone size={16} />
                                                                </button>
                                                            )}
                                                            {aviso.status === 'publicado' && (
                                                                <button onClick={() => setConfirmarArquivar(aviso)} title="Arquivar" className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                                                    <Archive size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            {expandido && (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/30">
                                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line mb-3">{aviso.mensagem}</p>
                                                        {aviso.unidade_geradora_site_id && (
                                                            <p className="text-xs text-gray-400 mb-2">Geradora: {aviso.unidade_geradora_site_id}</p>
                                                        )}
                                                        {aviso.url_anexo && (
                                                            <a href={aviso.url_anexo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary mb-4">
                                                                Ver anexo <ExternalLink size={12} />
                                                            </a>
                                                        )}
                                                        {carregandoDetalhe && !detalhes.has(aviso.id) ? (
                                                            <p className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Carregando confirmações...</p>
                                                        ) : (
                                                            <div className="max-h-56 overflow-y-auto">
                                                                <table className="w-full text-left text-xs">
                                                                    <thead className="text-gray-400 uppercase tracking-wide">
                                                                        <tr>
                                                                            <th className="py-1.5 pr-4">Colaborador</th>
                                                                            <th className="py-1.5 pr-4">Visualizou em</th>
                                                                            <th className="py-1.5">Confirmou em</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                                                        {(detalhes.get(aviso.id) || []).map((c) => (
                                                                            <tr key={c.id}>
                                                                                <td className="py-1.5 pr-4 text-gray-700 dark:text-gray-300">{c.profiles?.full_name || c.profile_id}</td>
                                                                                <td className="py-1.5 pr-4 text-gray-500">{dataHoraBr(c.visualizado_em)}</td>
                                                                                <td className="py-1.5 text-gray-500">{dataHoraBr(c.confirmado_em)}</td>
                                                                            </tr>
                                                                        ))}
                                                                        {(detalhes.get(aviso.id) || []).length === 0 && !carregandoDetalhe && (
                                                                            <tr><td colSpan={3} className="py-2 text-gray-400">Ninguém visualizou este aviso ainda.</td></tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {modalAberto && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {editando ? 'Editar aviso' : 'Novo aviso do sistema'}
                            </h3>
                            <button onClick={() => setModalAberto(false)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="px-6 pb-6 space-y-4">
                            {editando && editando.status === 'publicado' && (
                                <p className="text-xs bg-secondary/10 text-secondary-dark border border-secondary/20 rounded-lg px-3 py-2">
                                    Este aviso já foi publicado. Salvar aqui cria uma nova versão e arquiva a atual — quem já confirmou vai ver a nova versão como pendente de novo.
                                </p>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Título</label>
                                <input
                                    value={form.titulo}
                                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Modelo</label>
                                <select
                                    value={form.modelo}
                                    onChange={(e) => {
                                        const modelo = e.target.value as AvisoSistema['modelo'];
                                        // Campo "Geradora" some da tela quando o modelo não é "risco" — limpa o
                                        // valor junto, senão um site_id_api digitado antes fica órfão no state e
                                        // vai pro banco escondido num aviso de "novidade" sem ninguém perceber.
                                        setForm((prev) => ({ ...prev, modelo, unidade_geradora_site_id: modelo === 'risco' ? prev.unidade_geradora_site_id : '' }));
                                    }}
                                    className="w-full p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                >
                                    <option value="novidade">Novidade</option>
                                    <option value="risco">Risco</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Alvo</label>
                                <select
                                    value={form.departamento_alvo}
                                    onChange={(e) => setForm({ ...form, departamento_alvo: e.target.value })}
                                    className="w-full p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                >
                                    <option value={TODO_STAFF}>Todo o staff</option>
                                    {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            {form.modelo === 'risco' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Geradora (opcional)</label>
                                    <input
                                        value={form.unidade_geradora_site_id}
                                        onChange={(e) => setForm({ ...form, unidade_geradora_site_id: e.target.value })}
                                        placeholder="site_id_api da usina (ex.: Qgbt3)"
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        Linka o aviso à usina certa. Filtro/agrupamento por geradora no painel fica para uma fase futura — por ora é só rastreabilidade.
                                    </p>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Mensagem</label>
                                <textarea
                                    value={form.mensagem}
                                    onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                                    rows={5}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Anexo (opcional)</label>
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={form.url_anexo}
                                        onChange={(e) => setForm({ ...form, url_anexo: e.target.value })}
                                        placeholder="Cole um link ou envie um arquivo"
                                        className="flex-1 min-w-0 px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                    <label className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${enviandoAnexo ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-wait' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer'}`}>
                                        {enviandoAnexo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                                        {enviandoAnexo ? 'Enviando...' : 'Enviar arquivo'}
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            className="hidden"
                                            disabled={enviandoAnexo}
                                            onChange={enviarAnexo}
                                        />
                                    </label>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Vigência de</label>
                                    <input
                                        type="datetime-local"
                                        value={form.data_inicio}
                                        onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Vigência até</label>
                                    <input
                                        type="datetime-local"
                                        value={form.data_fim}
                                        onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={form.bloqueante}
                                    onChange={(e) => setForm({ ...form, bloqueante: e.target.checked })}
                                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/30"
                                />
                                Exigir confirmação antes de continuar (bloqueante)
                            </label>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 flex justify-end gap-2">
                            <button
                                onClick={() => salvar('rascunho')}
                                disabled={salvando}
                                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Salvar rascunho
                            </button>
                            <button
                                onClick={() => setConfirmarPublicar({})}
                                disabled={salvando}
                                className="px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                            >
                                {salvando && <Loader2 size={14} className="animate-spin" />}
                                Publicar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <ConfirmationModal
                isOpen={!!confirmarPublicar}
                onClose={() => setConfirmarPublicar(null)}
                onConfirm={() => (confirmarPublicar?.id ? publicarRascunho(confirmarPublicar.id) : salvar('publicado'))}
                title="Publicar este aviso?"
                description="Ele vai bloquear o trabalho de quem for alcançado (todo o staff ou o departamento escolhido) até confirmar leitura, se marcado como bloqueante."
                confirmText="Publicar"
                variant="warning"
                isLoading={salvando}
            />

            <ConfirmationModal
                isOpen={!!confirmarArquivar}
                onClose={() => setConfirmarArquivar(null)}
                onConfirm={() => confirmarArquivar && arquivar(confirmarArquivar)}
                title="Arquivar este aviso?"
                description="Ele deixa de bloquear o time imediatamente. Esta ação não apaga o histórico de confirmações já registradas."
                confirmText="Arquivar"
                variant="danger"
            />
        </div>
    );
};

export default AvisosSistemaView;
